/* eslint-disable react/prop-types */

import React from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  ResponsiveContainer,
} from "recharts";
import type { Props as LineProps } from "recharts/types/cartesian/Line";
import moment from "moment";
import { Consciousness, type Observation } from "@/resources/types";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import { Box, Stack, Typography } from "@mui/material";
import { CONSC_LABELS } from "@/resources/constants";
import { type ObsMetricName, obsMetricNames, obsMetricToLabel } from "@/resources/observations_metricUtils";
import { obsMetricToMark } from "./ObservationChart_obsMetricValueToMark";
import { css, type Theme } from "@/theme";
import { getValueFromObs, type GetValueFromObsProps } from "@/components/ObservationSummary";
import { ObsTabs, useObsTabsCtx } from "./ObservationTabs_ctx";

const styles = {
  zone1: (theme: Theme) => css`
    position: absolute;
    top: 0;
    left: 0;
    height: calc(100% - 35px);
    width: 100%;
    background-color: ${theme.palette.common.purple[50]};
  `,
  zone2: (theme: Theme) => css`
    position: absolute;
    top: calc(100% / 7 + 8px);
    left: 0;
    height: calc(100% / 7 * 5 - 10px);
    width: 100%;
    background-color: ${theme.palette.common.red[50]};
    border-top: 1px dotted black;
    border-bottom: 1px dotted black;
  `,
  zone3: (theme: Theme) => css`
    position: absolute;
    top: calc(100% / 5);
    left: 0;
    height: calc(100% / 5 * 3);
    width: 100%;
    background-color: ${theme.palette.common.white};
    border-top: 1px dotted black;
    border-bottom: 1px dotted black;
  `,
};

/**
 * - 1 - very bad (low)
 * - 2 - bad (low)
 * - 3 - good
 * - 4 - good
 * - 5 - good
 * - 6 - bad (high)
 * - 7 - very bad (high)
 *
 * we have three copies of "good" to make it more visible\
 * and evident in the line chart
 */
type Mark = 1 | 2 | 3 | 4 | 5 | 6 | 7;
type ObservationWithMarksVitalsSigns = {
  [obsMetricNames.respirations]?: Mark;
  [obsMetricNames.o2Sats]?: Mark;
  [obsMetricNames.o2Flow]?: Mark;
  [obsMetricNames.heartRate]?: Mark;
  [obsMetricNames.bloodPressure]?: Mark;
  [obsMetricNames.temperature]?: Mark;
  [obsMetricNames.consciousness]?: Mark;
  [obsMetricNames.bloodGlucose]?: Mark;
  [obsMetricNames.bloodAlcohol]?: Mark;
  // [ obsMetricNames.riskScore ]?: Mark;
  [obsMetricNames.painScore]?: Mark;
};
type ObservationWithMarksBedside = {
  [obsMetricNames.bedsideBloodPressureLaying]?: Mark;
  [obsMetricNames.bedsideBloodPressureLeftArm]?: Mark;
  [obsMetricNames.bedsideBloodPressureRightArm]?: Mark;
  [obsMetricNames.bedsideBloodPressureStanding]?: Mark;
  [obsMetricNames.bedsideHeartRate]?: Mark;
};
type ObservationWithMarksVentilator = {
  [obsMetricNames.ventilatorEndTidalCarbonDioxide]?: Mark;
  [obsMetricNames.ventilatorFrequencyRate]?: Mark;
  [obsMetricNames.ventilatorMinuteVentilation]?: Mark;
  [obsMetricNames.ventilatorPeakPressure]?: Mark;
  [obsMetricNames.ventilatorTidalVolume]?: Mark;
};

type ObservationWithMarks = (
  ObservationWithMarksVitalsSigns | ObservationWithMarksBedside | ObservationWithMarksVentilator
) & { original: Observation; };

type ToRequiredWithNumberOrNullValue<ObsWithMarks> = {
  [x in keyof Required<ObsWithMarks>]: number | null;
}
type CreatedAndWithMarks = {
  created: Observation["created"];
  withMarks: ObservationWithMarks;
}
type ObservationInChartVitalsSigns = CreatedAndWithMarks & ToRequiredWithNumberOrNullValue<ObservationWithMarksVitalsSigns>;
type ObservationInChartBedside = CreatedAndWithMarks & ToRequiredWithNumberOrNullValue<ObservationWithMarksBedside>;
type ObservationInChartVentilator = CreatedAndWithMarks & ToRequiredWithNumberOrNullValue<ObservationWithMarksVentilator>;

type ObservationInChart = ObservationInChartVitalsSigns | ObservationInChartBedside | ObservationInChartVentilator

/**
   * spacing multiplier, helps to alter dots position so that\
   * they dont overlap in the graph
   */
const MULT_BASE = 40;
const MULT = 7 * MULT_BASE;

// eslint-disable-next-line complexity
const observationToChartShapeVitalSigns = (o: Observation): ObservationInChartVitalsSigns => {
  const w: ObservationWithMarks = {
    respirations: obsMetricToMark({ type: "respirations", value: o.respirations }),
    o2Sats: obsMetricToMark({ type: "o2Sats", value: o.o2Sats }),
    o2Flow: obsMetricToMark({ type: "o2Flow", value: o.o2Flow }),
    heartRate: obsMetricToMark({ type: "heartRate", value: o.heartRate }),
    bloodPressure: obsMetricToMark({ type: "bloodPressure", value: o.bloodPressure }),
    temperature: obsMetricToMark({ type: "temperature", value: o.temperature }),
    consciousness: obsMetricToMark({ type: "consciousness", value: o.consciousness }),
    bloodGlucose: obsMetricToMark({ type: "bloodGlucose", value: o.bloodGlucose }),
    bloodAlcohol: obsMetricToMark({ type: "bloodAlcohol", value: o.bloodAlcohol }),
    painScore: obsMetricToMark({ type: "painScore", value: o.painScore }),
    // riskScore: obsMetricToMark({ type: "riskScore", value: o.riskScore}),
    original: o,
  };

  return {
    created: o.created,
    respirations: w.respirations === undefined ? null : ((w.respirations - 1) * MULT + MULT_BASE),
    o2Sats: w.o2Sats === undefined ? null : ((w.o2Sats - 1) * MULT + MULT_BASE * 2),
    o2Flow: w.o2Flow === undefined ? null : ((w.o2Flow - 1) * MULT + MULT_BASE * 3),
    heartRate: w.heartRate === undefined ? null : ((w.heartRate - 1) * MULT + MULT_BASE * 4),
    bloodPressure: w.bloodPressure === undefined ? null : ((w.bloodPressure - 1) * MULT + MULT_BASE * 5),
    temperature: w.temperature === undefined ? null : ((w.temperature - 1) * MULT + MULT_BASE * 6),
    consciousness: w.consciousness === undefined ? null : ((w.consciousness - 1) * MULT + MULT_BASE * 7),
    // riskScore: w.riskScore === undefined ? null : ( ( w.riskScore - 1 ) * MULT + MULT_BASE * 8 ),
    bloodGlucose: w.bloodGlucose === undefined ? null : ((w.bloodGlucose - 1) * MULT + MULT_BASE * 8),
    bloodAlcohol: w.bloodAlcohol === undefined ? null : ((w.bloodAlcohol - 1) * MULT + MULT_BASE * 9),
    painScore: w.painScore === undefined ? null : ((w.painScore - 1) * MULT + MULT_BASE * 10),
    withMarks: w,
  };
};

// eslint-disable-next-line complexity
const observationToChartShapeBedside = (o: Observation): ObservationInChartBedside => {
  const w: ObservationWithMarks = {
    bedsideBloodPressureLaying: obsMetricToMark({ type: "bedsideBloodPressureLaying", value: o.bedside?.bloodPressure?.laying }),
    bedsideBloodPressureLeftArm: obsMetricToMark({ type: "bedsideBloodPressureLeftArm", value: o.bedside?.bloodPressure?.leftArm }),
    bedsideBloodPressureRightArm: obsMetricToMark({ type: "bedsideBloodPressureRightArm", value: o.bedside?.bloodPressure?.rightArm }),
    bedsideBloodPressureStanding: obsMetricToMark({ type: "bedsideBloodPressureStanding", value: o.bedside?.bloodPressure?.standing }),
    bedsideHeartRate: obsMetricToMark({ type: "bedsideHeartRate", value: Number(o.bedside?.heartRate) }),
    original: o,
  };

  return {
    created: o.created,
    bedsideBloodPressureLaying: w.bedsideBloodPressureLaying === undefined ? null : ((w.bedsideBloodPressureLaying - 1) * MULT + MULT_BASE),
    bedsideBloodPressureLeftArm: w.bedsideBloodPressureLeftArm === undefined ? null : ((w.bedsideBloodPressureLeftArm - 1) * MULT + MULT_BASE * 2),
    bedsideBloodPressureRightArm: w.bedsideBloodPressureRightArm === undefined ? null : ((w.bedsideBloodPressureRightArm - 1) * MULT + MULT_BASE * 3),
    bedsideBloodPressureStanding: w.bedsideBloodPressureStanding === undefined ? null : ((w.bedsideBloodPressureStanding - 1) * MULT + MULT_BASE * 4),
    bedsideHeartRate: w.bedsideHeartRate === undefined ? null : ((w.bedsideHeartRate - 1) * MULT + MULT_BASE * 5),
    withMarks: w,
  };
};
// eslint-disable-next-line complexity
const observationToChartShapeVentilator = (o: Observation): ObservationInChartVentilator => {
  const w: ObservationWithMarks = {
    ventilatorEndTidalCarbonDioxide: obsMetricToMark({ type: "ventilatorEndTidalCarbonDioxide", value: o.ventilator?.endTidalCarbonDioxide }),
    ventilatorFrequencyRate: obsMetricToMark({ type: "ventilatorFrequencyRate", value: o.ventilator?.frequencyRate }),
    ventilatorMinuteVentilation: obsMetricToMark({ type: "ventilatorMinuteVentilation", value: o.ventilator?.minuteVentilation }),
    ventilatorPeakPressure: obsMetricToMark({ type: "ventilatorPeakPressure", value: o.ventilator?.peakPressure }),
    ventilatorTidalVolume: obsMetricToMark({ type: "ventilatorTidalVolume", value: Number(o.ventilator?.tidalVolume) }),
    original: o,
  };

  return {
    created: o.created,
    ventilatorEndTidalCarbonDioxide: w.ventilatorEndTidalCarbonDioxide === undefined ? null : ((w.ventilatorEndTidalCarbonDioxide - 1) * MULT + MULT_BASE),
    ventilatorFrequencyRate: w.ventilatorFrequencyRate === undefined ? null : ((w.ventilatorFrequencyRate - 1) * MULT + MULT_BASE * 2),
    ventilatorMinuteVentilation: w.ventilatorMinuteVentilation === undefined ? null : ((w.ventilatorMinuteVentilation - 1) * MULT + MULT_BASE * 3),
    ventilatorPeakPressure: w.ventilatorPeakPressure === undefined ? null : ((w.ventilatorPeakPressure - 1) * MULT + MULT_BASE * 4),
    ventilatorTidalVolume: w.ventilatorTidalVolume === undefined ? null : ((w.ventilatorTidalVolume - 1) * MULT + MULT_BASE * 5),
    withMarks: w,
  };
};

const obsChartLineMetaDataVitalSigns = [
  { prop: obsMetricNames.respirations, color: "#ff8680" },
  { prop: obsMetricNames.o2Sats, color: "#f8a066" },
  { prop: obsMetricNames.o2Flow, color: "#f6c564" },
  { prop: obsMetricNames.heartRate, color: "#54bfa0" },
  { prop: obsMetricNames.bloodPressure, color: "#66b9f8" },
  { prop: obsMetricNames.temperature, color: "#bd66f8" },
  { prop: obsMetricNames.consciousness, color: "#66470a" },
  { prop: obsMetricNames.bloodGlucose, color: "#78010f" },
  // { prop: obsMetricNames.riskScore, color: "#f10f78"},
  { prop: obsMetricNames.bloodAlcohol, color: "#11470a" },
  { prop: obsMetricNames.painScore, color: "#78f10f" },
] as const;

const obsChartLineMetaDataBedside = [
  { prop: obsMetricNames.bedsideBloodPressureLaying, color: "#ff8680" },
  { prop: obsMetricNames.bedsideBloodPressureLeftArm, color: "#f8a066" },
  { prop: obsMetricNames.bedsideBloodPressureRightArm, color: "#f6c564" },
  { prop: obsMetricNames.bedsideBloodPressureStanding, color: "#54bfa0" },
  { prop: obsMetricNames.bedsideHeartRate, color: "#66b9f8" },
] as const;

const obsChartLineMetaDataVentilator = [
  { prop: obsMetricNames.ventilatorEndTidalCarbonDioxide, color: "#ff8680" },
  { prop: obsMetricNames.ventilatorFrequencyRate, color: "#f8a066" },
  { prop: obsMetricNames.ventilatorMinuteVentilation, color: "#f6c564" },
  { prop: obsMetricNames.ventilatorPeakPressure, color: "#54bfa0" },
  { prop: obsMetricNames.ventilatorTidalVolume, color: "#66b9f8" },
] as const;

export const getObsChartLineColor = (metric: ObsMetricName, type: ObsTabs): string => {
  const result = {
    [ObsTabs.VITALS_SIGNS]: (obsChartLineMetaDataVitalSigns.find(it => it.prop === metric) || obsChartLineMetaDataVitalSigns[0]).color,
    [ObsTabs.NEUROLOGICAL]: "#ff8680",
    [ObsTabs.BEDSIDE]: (obsChartLineMetaDataBedside.find(it => it.prop === metric) || obsChartLineMetaDataBedside[0]).color,
    [ObsTabs.VENTILATOR]: (obsChartLineMetaDataVentilator.find(it => it.prop === metric) || obsChartLineMetaDataVentilator[0]).color,
    [ObsTabs.URINALYSIS]: "#ff8680",
  }[type];

  return result;
};

export type ObservationChartProps = {
  observations: Observation[];
};

const DotTooltipTitle = React.memo<{ metric: ObsMetricName; value: string }>(p => (
  <Stack>
    <Typography variant='h6'>{obsMetricToLabel(p.metric)}</Typography>
    <Typography variant='caption'>{p.value}</Typography>
  </Stack>
));
DotTooltipTitle.displayName = "ObservationChart/DotTooltipTitle";

// eslint-disable-next-line complexity
const CustomizedDot: NonNullable<LineProps["dot"]> = (props) => {
  const { cx, cy, stroke, payload, dataKey, type } = props;
  const key = String(dataKey) as ObsMetricName;
  const { withMarks: { original } } = payload as { withMarks: ObservationInChart["withMarks"] };
  const value = getValueFromObs({ type, observation: original, key } as GetValueFromObsProps);

  const isUndefinedRange = typeof value === "object" && (!value.diastolic || !value.systolic);
  if ((key === "consciousness" && value === Consciousness.NA) || value === undefined || isUndefinedRange) {
    return <>{null}</>;
  }

  const keysWithMinMaxValues = [
    "bloodPressure",
    "bedsideBloodPressureLaying",
    "bedsideBloodPressureLeftArm",
    "bedsideBloodPressureRightArm",
    "bedsideBloodPressureStanding",
    "ventilatorEndTidalCarbonDioxide",
  ];
  const cleanedValue = typeof value === "object" && keysWithMinMaxValues.includes(key)
    ? `Max: ${ value.systolic || value }, min: ${ value.diastolic }`
    : (
      key === "consciousness"
        ? CONSC_LABELS[String(value) as Consciousness]
        : String(value)
    );

  return (
    <Tooltip
      disableFocusListener
      disableTouchListener
      title={<DotTooltipTitle metric={key} value={cleanedValue} />}
      PopperProps={{
        sx: theme => ({
          [`& .${tooltipClasses.tooltip}`]: {
            backgroundColor: "#f5f5f9",
            color: theme.palette.getContrastText("#f5f5f9"),
          }
        })
      }}
    >
      <circle
        cx={cx}
        cy={cy}
        r={3}
        stroke={stroke}
        strokeWidth={1}
        fill={stroke}
        style={{ cursor: "pointer" }}
      />
    </Tooltip>
  );
};
// biome-ignore lint/suspicious/noExplicitAny: <explanation>
(CustomizedDot as any).displayName = "ObservationChart/CustomizedDot";

const ObservationChartBackground = () => {
  return (
    <Box css={styles.zone1}>
      <Box css={styles.zone2}>
        <Box css={styles.zone3} />
      </Box>
    </Box>
  );
};

const ObservationChart: React.FC<ObservationChartProps> = React.memo(({ observations }) => {
  const { value: { tab: type } } = useObsTabsCtx();
  const [hoveredProp, setHoveredProp] = React.useState<ObsMetricName | null>(null);

  const { chartObservations, lineMetadata } = React.useMemo(() => {
    return {
      [ObsTabs.VITALS_SIGNS]: { chartObservations: observations.map(observationToChartShapeVitalSigns), lineMetadata: obsChartLineMetaDataVitalSigns },
      [ObsTabs.NEUROLOGICAL]: { chartObservations: [], lineMetadata: [] },
      [ObsTabs.BEDSIDE]: { chartObservations: observations.map(observationToChartShapeBedside), lineMetadata: obsChartLineMetaDataBedside },
      [ObsTabs.VENTILATOR]: { chartObservations: observations.map(observationToChartShapeVentilator), lineMetadata: obsChartLineMetaDataVentilator },
      [ObsTabs.URINALYSIS]: { chartObservations: [], lineMetadata: [] },
    }[type];
  }, [type]);

  const getValueFromObsChartByPropName = React.useCallback((
    chartData: Omit<ObservationInChart, "created" | "withMarks">,
    propName: keyof ObservationInChartVitalsSigns | keyof ObservationInChartBedside | keyof ObservationInChartVentilator
  ) => {
    return {
      [ObsTabs.VITALS_SIGNS]: (chartData as ObservationInChartVitalsSigns)[propName as keyof ObservationInChartVitalsSigns],
      [ObsTabs.NEUROLOGICAL]: [],
      [ObsTabs.BEDSIDE]: (chartData as ObservationInChartBedside)[propName as keyof ObservationInChartBedside],
      [ObsTabs.VENTILATOR]: (chartData as ObservationInChartVentilator)[propName as keyof ObservationInChartVentilator],
      [ObsTabs.URINALYSIS]: (chartData as ObservationInChartVentilator)[propName as keyof ObservationInChartVentilator],
    }[type];
  }, [type]);

  return (
    <>
      <ObservationChartBackground />
      <ResponsiveContainer width="100%" height='100%' >
        <LineChart data={chartObservations}>

          <XAxis
            dataKey="created"
            padding={{ right: 20, left: 60 }}
            scale="point"
            tick={{ fontSize: 12, fontWeight: 700 }}
            tickFormatter={o => moment(o).format("DD/MM/YY @ HH:mm")}
          />
          <YAxis domain={[0, 2000]} hide />

          {
            lineMetadata.map(({ color, prop }) => {
              if (chartObservations.some(it => getValueFromObsChartByPropName(it, prop) !== null) === false) return null;

              const mark = (getValueFromObsChartByPropName(chartObservations[chartObservations.length - 1].withMarks, prop) || 1) as number;
              const shouldBeGreyedOut = mark >= 3 && mark <= 5;
              const stroke = (hoveredProp === prop || shouldBeGreyedOut === false)
                ? color
                : "#dddddd";

              return (
                <Line
                  key={prop}
                  type="monotone"
                  strokeWidth='5'
                  connectNulls
                  stroke={stroke}
                  dataKey={prop}
                  isAnimationActive={false}
                  dot={p => <CustomizedDot {...p} type={type} />}
                  onMouseEnter={() => setHoveredProp(prop)}
                  onMouseLeave={() => setHoveredProp(null)}
                  style={{ cursor: "pointer" }}
                />
              );
            })
          }
        </LineChart>
      </ResponsiveContainer>
    </>
  );
});
ObservationChart.displayName = "components/ObservationChart/ObservationChart";


export default ObservationChart;
